import type { FC } from 'react';

import type { CarePlanEvidence } from '@eluve/api-contract';
import {
  Button,
  Caption,
  Icon,
  LoadingIndicator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Stack,
  Tag,
  Text,
} from '@eluve/components';
import type { IconName, TagProps } from '@eluve/components';
import { useAppointmentTranscriptQuery } from '@eluve/frontend-appointment-hooks';

import { type EvidenceLocation, locateEvidence } from './care-plan-evidence';

export type CarePlanEvidenceSelection = {
  label: string;
  itemType: 'goal' | 'measure' | 'action-item';
  evidence: CarePlanEvidence;
};

export type CarePlanEvidenceSelectionHandler = (
  selection: CarePlanEvidenceSelection,
  triggerElement: HTMLElement,
) => void;

/** Which source surface the drawer's open-source action targets. */
export type EvidenceSourceTarget = 'transcript' | 'note';

const ITEM_TYPE_LABEL: Record<CarePlanEvidenceSelection['itemType'], string> = {
  goal: 'Goal',
  measure: 'Objective measure',
  'action-item': 'Action item',
};

type SourcePresentation = {
  icon: IconName;
  typeLabel: string;
  chip: { label: string; color: TagProps['color'] } | null;
};

/**
 * `transcript` is a verbatim, offset-verified excerpt. Everything else is the
 * note's synthesized finding text (goals/measures share the Note's Goal
 * resources), so its home is the clinical note.
 */
const sourcePresentation = (
  sourceType: CarePlanEvidence['sourceType'],
  hasSpan: boolean,
): SourcePresentation => {
  if (sourceType === 'transcript') {
    return {
      icon: 'AudioLines',
      typeLabel: 'Visit transcript',
      // "Located" only claims a pinned spot when a span actually exists.
      chip: hasSpan ? { label: 'Located', color: 'positive' } : null,
    };
  }
  return { icon: 'FileText', typeLabel: 'Clinical note', chip: null };
};

const VISIT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const formatVisitDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return VISIT_DATE_FORMAT.format(date);
};

const TranscriptContext: FC<{
  loading: boolean;
  hasError: boolean;
  location: EvidenceLocation | null;
}> = ({ loading, hasError, location }) => {
  if (loading) {
    return (
      <Stack direction="horizontal" gap={2} align="center">
        <LoadingIndicator />
        <Text size="sm" color="secondary">
          Loading transcript…
        </Text>
      </Stack>
    );
  }

  if (hasError) {
    return (
      <Text size="sm" color="secondary">
        Transcript unavailable
      </Text>
    );
  }

  if (!location) {
    return null;
  }

  return (
    <Stack direction="vertical" gap={1}>
      <Caption>In transcript</Caption>
      <Text
        size="sm"
        className="rounded-lg border border-border-secondary bg-bg-secondary p-3 leading-relaxed whitespace-pre-wrap"
      >
        {location.before}
        <mark className="rounded-sm bg-bg-warning-subtle px-0.5 text-text-primary">
          {location.highlight}
        </mark>
        {location.after}
      </Text>
    </Stack>
  );
};

const EvidenceBody: FC<{
  tenantId: string;
  currentAppointmentId: string;
  evidence: CarePlanEvidence;
  onOpenSource?: (target: EvidenceSourceTarget) => void;
}> = ({ tenantId, currentAppointmentId, evidence, onOpenSource }) => {
  // Only a verified verbatim excerpt gets in-drawer transcript context; the
  // note's synthesized finding text has no transcript line to anchor to.
  const isTranscriptExcerpt = evidence.sourceType === 'transcript';
  const { loading, error, transcript, startDate } =
    useAppointmentTranscriptQuery({
      tenantId,
      appointmentId: evidence.sourceAppointmentId,
    });

  const presentation = sourcePresentation(
    evidence.sourceType,
    evidence.span !== null,
  );
  const isThisVisit = evidence.sourceAppointmentId === currentAppointmentId;
  const visitDate = formatVisitDate(startDate);
  const attribution = [isThisVisit ? 'This visit' : 'Earlier visit', visitDate]
    .filter(Boolean)
    .join(' · ');

  const hasError = Boolean(error) || transcript.length === 0;
  const location =
    isTranscriptExcerpt && !loading && !hasError
      ? locateEvidence({
          transcript,
          quote: evidence.quote,
          span: evidence.span,
        })
      : null;

  // Both tabs only render the current visit, so opening the source is offered
  // only for this-visit evidence. Transcript excerpts open the transcript;
  // note-derived goals open the Note.
  const canOpenSource = Boolean(onOpenSource && isThisVisit);

  return (
    <Stack direction="vertical" gap={5} className="px-4 pb-6">
      <Stack direction="vertical" gap={1.5}>
        <Caption>Source</Caption>
        <Stack
          direction="horizontal"
          align="center"
          gap={3}
          className="rounded-lg border border-border-secondary bg-bg-secondary p-3"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-bg-primary text-text-secondary">
            <Icon name={presentation.icon} size="sm" />
          </div>
          <Stack direction="vertical" gap={0.5} className="min-w-0 flex-1">
            <Stack direction="horizontal" align="center" gap={2} wrap>
              <Text size="sm" className="font-medium">
                {presentation.typeLabel}
              </Text>
              {presentation.chip && (
                <Tag
                  size="xs"
                  pill
                  subtle
                  color={presentation.chip.color}
                  label={presentation.chip.label}
                />
              )}
            </Stack>
            <Text size="xs" color="secondary">
              {attribution}
            </Text>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="vertical" gap={1}>
        <Caption>Supporting quote</Caption>
        <blockquote className="rounded-lg border-l-4 border-border-insights-subtle bg-bg-insights-subtle p-3">
          <Text size="sm" className="whitespace-pre-wrap">
            {evidence.quote}
          </Text>
        </blockquote>
      </Stack>

      {isTranscriptExcerpt && (
        <TranscriptContext
          loading={loading}
          hasError={hasError}
          location={location}
        />
      )}

      {canOpenSource && (
        <Button
          size="sm"
          variant="outline"
          className="w-fit"
          onClick={() =>
            onOpenSource?.(isTranscriptExcerpt ? 'transcript' : 'note')
          }
        >
          <Icon name="ExternalLink" size="auto" />
          {isTranscriptExcerpt ? 'Open in transcript' : 'Open in note'}
        </Button>
      )}
    </Stack>
  );
};

export const CarePlanEvidenceDrawer: FC<{
  tenantId: string;
  currentAppointmentId: string;
  selection: CarePlanEvidenceSelection | null;
  triggerElement: HTMLElement | null;
  onClose: () => void;
  onOpenSource?: (target: EvidenceSourceTarget) => void;
}> = ({
  tenantId,
  currentAppointmentId,
  selection,
  triggerElement,
  onClose,
  onOpenSource,
}) => {
  return (
    <Sheet
      open={selection !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-lg"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerElement?.focus();
        }}
      >
        <SheetHeader className="border-b border-border-secondary pr-14">
          <SheetTitle>{selection?.label ?? 'Care Plan evidence'}</SheetTitle>
          <SheetDescription>
            {selection
              ? `${ITEM_TYPE_LABEL[selection.itemType]} evidence`
              : 'Care Plan evidence'}
          </SheetDescription>
        </SheetHeader>

        {selection && (
          <EvidenceBody
            tenantId={tenantId}
            currentAppointmentId={currentAppointmentId}
            evidence={selection.evidence}
            onOpenSource={onOpenSource}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};
